<script setup lang="ts">
// An encode's form (EditDrawer): its input, video and audio settings
// (EncodeSettings), slate, behaviour on signal loss, and central's encoding
// templates.
import { computed, onMounted, reactive, ref } from 'vue'
import { api, central, base, type EditTarget, type Encode, type EncodeTemplate } from '../../api'
import { apiEncoding, destinationUse, formDefaults } from '../../encoding'
import { newId, slug } from '../../util'
import ChannelSelect from '../ChannelSelect.vue'
import EncodeSettings from '../EncodeSettings.vue'
import VerticalSettings from './VerticalSettings.vue'
import LadderSettings from './LadderSettings.vue'
import ScheduleEditor from './ScheduleEditor.vue'
import { newBreak } from '../../scte35'
import { clone, ipKinds, useEditContext, useMedia } from './context'

const props = defineProps<{ target: EditTarget }>()
const t = props.target
const { node, cfg, gpus } = useEditContext(t)
function blankEncode(): Encode {
    return {
        id: '',
        name: '',
        input_id: cfg.value.inputs[0]?.id ?? '',
        enabled: true,
        video: {
            codec: 'libx264',
            gpu: 0,
            preset: 'veryfast',
            profile: 'high',
            rc: 'cbr',
            bitrate_k: 6000,
            gop: 120,
            bframes: 2,
            fps: '',
            pix_fmt: '',
            level: '',
            scale_algo: '',
        },
        audio: [{ name: 'program', codec: 'aac', bitrate_k: 192, layout: 'stereo', channel_map: [0, 1] }],
        advanced: '',
    }
}
const encode = reactive<Encode>(
    formDefaults(
        t.kind === 'encode' ? clone(cfg.value.encodes.find((e) => e.id === t.id) ?? blankEncode()) : blankEncode(),
    ),
)
// what this encode's destinations are (platforms, a contribution link), for
// the encode lab's guidance in its settings
const use = computed(() =>
    encode.id ? destinationUse(cfg.value.destinations.filter((d) => d.encode_id === encode.id)) : null,
)
// passthrough is offered for IP inputs only (a DeckLink or test input has no stream to copy)
// the checks of what it sends, each opted in (DECISIONS 2026-10-17)
const probeChecks = [
    { key: 'check_black', label: 'black picture' },
    { key: 'check_frozen', label: 'frozen picture' },
    { key: 'check_silent', label: 'silent track' },
] as const
const inputIsIP = computed(() => ipKinds.includes(cfg.value.inputs.find((i) => i.id === encode.input_id)?.kind ?? ''))
// the input brings cues of its own: an IP feed's SCTE-35, an SDI feed's SCTE-104
const inputHasCues = computed(() => {
    const i = cfg.value.inputs.find((i) => i.id === encode.input_id)
    return inputIsIP.value || (i?.kind === 'decklink' && !!i.scte104)
})
// on_loss and its picture as one select value: "", black, stop or picture:<media id>
const lossChoice = computed({
    get: () => (encode.on_loss === 'picture' ? 'picture:' + (encode.loss_media_id ?? '') : (encode.on_loss ?? '')),
    set: (v: string) => {
        if (v.startsWith('picture:')) {
            encode.on_loss = 'picture'
            encode.loss_media_id = v.slice(8)
        } else {
            encode.on_loss = v as Encode['on_loss']
            encode.loss_media_id = undefined
        }
    },
})
// Twitch Enhanced Broadcasting: Twitch picks the renditions, the form only
// the channel's key, the caps, the GPU and the stereo pair
const isTwitch = computed({
    get: () => !!encode.twitch,
    set: (on: boolean) => {
        encode.twitch = on ? { stream_key: '', max_tracks: 0, max_bitrate_k: 0, protocol: '' } : undefined
        if (on) {
            encode.passthrough = false
            encode.video.codec = 'h264_nvenc'
            // Twitch takes one stereo pair: the first track's first two channels
            const a = (encode.audio[0] ??= {
                name: 'program',
                codec: 'aac',
                bitrate_k: 160,
                layout: 'stereo',
                channel_map: [0, 1],
            })
            const first = a.channel_map[0] ?? 0
            a.channel_map = [first, a.channel_map[1] ?? first + 1]
            a.layout = 'stereo'
        }
    },
})
const zone = Intl.DateTimeFormat().resolvedOptions().timeZone
// ad markers on or off (an SCTE-35 stream in what it sends)
const cuesOn = computed({
    get: () => !!encode.scte35,
    set: (on: boolean) => {
        encode.scte35 = on ? { mode: 'splice_insert' } : undefined
    },
})
const media = useMedia()
// the input's shape, for the vertical sketch (16:9 when it follows its source)
const inputFormat = computed(() => {
    const i = cfg.value.inputs.find((x) => x.id === encode.input_id)
    return { w: i?.width || 16, h: i?.height || 9 }
})
// ffmpeg option autocomplete for the tuning field
const ffopts = ref<{ name: string; help: string }[]>([])
const encTemplateId = ref<number | ''>('')
const encTemplates = ref<EncodeTemplate[]>([])
onMounted(async () => {
    try {
        ffopts.value = await api.ffmpegOptions()
    } catch {
        /* offline */
    }
    try {
        encTemplates.value = await api.encodeTemplates()
    } catch {
        /* not enrolled, or central unreachable: no templates */
    }
})
// central's encode templates: picking one copies its settings into the form
// (the GPU stays this node's choice); saving one needs central's user, so
// only through central
function applyEncTemplate() {
    const p = encTemplates.value.find((x) => x.id === encTemplateId.value)
    if (p) {
        // the template leaves empty fields out; the selects' "as the input" is ''
        encode.video = {
            fps: '',
            pix_fmt: '',
            level: '',
            scale_algo: '',
            profile: '',
            preset: '',
            ...clone(p.video),
            gpu: encode.video.gpu,
        }
        encode.audio = clone(p.audio)
        encode.advanced = p.advanced ?? ''
    }
}
const tplName = ref<string | null>(null)
const tplExisting = computed(() => encTemplates.value.find((x) => x.name === tplName.value?.trim()))
const busy = ref(false) // saving a template (the drawer's own save is EditDrawer's)
async function saveEncTemplate() {
    busy.value = true
    try {
        const { video, audio, advanced } = encodeSettings()
        // not the vertical picture: its media are this node's
        const body = { name: tplName.value!.trim(), video: { ...video, gpu: 0, vertical: undefined }, audio, advanced }
        const saved = tplExisting.value
            ? await central.updateTemplate({ ...body, id: tplExisting.value.id })
            : await central.saveTemplate(body)
        encTemplates.value = [...encTemplates.value.filter((x) => x.id !== saved.id), saved].sort((a, b) =>
            a.name.localeCompare(b.name),
        )
        tplName.value = null
        node.notify(`encoding template ${saved.name} saved on central`)
    } catch (e) {
        node.notify(e instanceof Error ? e.message : String(e), true)
    } finally {
        busy.value = false
    }
}
// a passthrough's delay buffer: the source's rate it is sized for (its
// video.bitrate_k; empty = 25000 until the node has measured the source,
// which grows the buffer in place); its own field, so toggling passthrough
// never sends the encode's bitrate as the figure
const bufferK = ref<number | ''>(encode.passthrough && encode.video.bitrate_k ? encode.video.bitrate_k : '')
// the form's video and audio as the API takes them
const encodeSettings = () => apiEncoding(encode.video, encode.audio, encode.advanced, encode.passthrough, bufferK.value)
async function save() {
    const id = encode.id || newId(slug(encode.name) + '-', new Set(cfg.value.encodes.map((e) => e.id)))
    // a passthrough copies the source: nothing to set but its buffer's figure
    const tw = encode.twitch
    const body = {
        ...encode,
        id,
        ...encodeSettings(),
        twitch: tw
            ? {
                  stream_key: tw.stream_key.trim(),
                  max_tracks: Number(tw.max_tracks) || undefined,
                  max_bitrate_k: Number(tw.max_bitrate_k) || undefined,
                  protocol: tw.protocol || undefined,
              }
            : undefined,
        passthrough: (inputIsIP.value && encode.passthrough) || undefined,
        scte35:
            encode.scte35 && !encode.passthrough && !encode.twitch
                ? {
                      mode: encode.scte35.mode,
                      preroll_ms: Number(encode.scte35.preroll_ms) || undefined,
                      breaks: encode.scte35.breaks?.length ? encode.scte35.breaks : undefined,
                      ignore_source: encode.scte35.ignore_source || undefined,
                  }
                : undefined,
        slate_text: encode.slate_text || undefined,
        on_loss: encode.on_loss || undefined,
        loss_media_id: encode.on_loss === 'picture' ? encode.loss_media_id : undefined,
    }
    await node.save('encodes', id, body)
    node.select({ kind: 'encode', id })
}
defineExpose({ save, snapshot: () => JSON.stringify(encode) })
</script>

<template>
    <div class="mb-2.5">
        <label>Name</label><input v-model="encode.name" required autofocus placeholder="Main 6M" />
    </div>
    <div class="grid grid-cols-2 gap-2.5">
        <div class="mb-2.5">
            <label>Input</label
            ><select v-model="encode.input_id">
                <option v-if="!encode.input_id" value="" disabled>— pick an input —</option>
                <option v-for="i in cfg.inputs" :key="i.id" :value="i.id">{{ i.name }}</option>
            </select>
        </div>
        <div v-if="encTemplates.length && !encode.passthrough && !encode.twitch" class="mb-2.5">
            <label>Start from encoding template</label
            ><select v-model="encTemplateId" @change="applyEncTemplate">
                <option value="">—</option>
                <option v-for="p in encTemplates" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
        </div>
    </div>
    <div class="mb-2.5">
        <label class="flex items-center gap-1.5"
            ><input v-model="isTwitch" type="checkbox" class="w-auto" /> Twitch Enhanced Broadcasting: Twitch picks the
            renditions</label
        >
        <div class="text-xs text-fg-3">
            At every start the node tells Twitch its GPU and the input's size; Twitch answers with up to five renditions
            (1080p60 H.264 down to 360p; 1440p60 HEVC on top for a Partner or Affiliate channel with a 1440p input),
            made here on NVENC and sent together on one connection. Needs an NVIDIA GPU; 59.94 and 29.97 go out as 60
            and 30, the rates Twitch takes.
        </div>
    </div>
    <template v-if="encode.twitch">
        <div class="grid grid-cols-2 gap-2.5">
            <div class="mb-2.5">
                <label>Stream key</label
                ><input
                    v-model="encode.twitch.stream_key"
                    type="password"
                    class="font-mono tabular-nums"
                    autocomplete="new-password"
                    required
                    placeholder="live_…"
                    title="Kept on the node and never shown again: type a new one to change it"
                />
            </div>
            <div class="mb-2.5">
                <label>GPU</label
                ><select v-model.number="encode.video.gpu">
                    <option v-for="g in gpus" :key="g.index" :value="g.index">{{ g.index }}: {{ g.name }}</option>
                    <option v-if="!gpus.some((g) => g.index === encode.video.gpu)" :value="encode.video.gpu">
                        {{ encode.video.gpu }}{{ gpus.length ? ' (not in this node)' : ' (no GPU detected)' }}
                    </option>
                </select>
            </div>
        </div>
        <div class="grid grid-cols-4 gap-2.5">
            <div class="mb-2.5">
                <label>Left channel</label
                ><ChannelSelect
                    v-model="encode.audio[0]!.channel_map[0]!"
                    class="w-full"
                    aria-label="Left channel"
                    data-test="twitch-left"
                />
            </div>
            <div class="mb-2.5">
                <label>Right channel</label
                ><ChannelSelect
                    v-model="encode.audio[0]!.channel_map[1]!"
                    class="w-full"
                    aria-label="Right channel"
                    data-test="twitch-right"
                />
            </div>
            <div class="mb-2.5">
                <label>Max renditions <span class="text-fg-3">(0 = Twitch)</span></label
                ><input v-model.number="encode.twitch.max_tracks" type="number" min="0" max="5" step="1" />
            </div>
            <div class="mb-2.5">
                <label>Max kb/s <span class="text-fg-3">(0 = Twitch)</span></label
                ><input v-model.number="encode.twitch.max_bitrate_k" type="number" min="0" step="500" />
            </div>
        </div>
        <div class="mb-2.5">
            <label>Connection</label
            ><select v-model="encode.twitch.protocol" class="max-w-[260px]">
                <option value="">RTMPS (encrypted)</option>
                <option value="rtmp">RTMP</option>
            </select>
        </div>
    </template>
    <div v-if="inputIsIP && !encode.twitch" class="mb-2.5">
        <label class="flex items-center gap-1.5"
            ><input v-model="encode.passthrough" type="checkbox" class="w-auto" /> Passthrough: pass the input's stream
            on as it is, no re-encode</label
        >
        <div class="text-xs text-fg-3">
            For distribution: every destination gets the source's own video and audio, unchanged, at almost no CPU. The
            node receives the input once; any number of passthroughs, encodes and SDI outputs of it share that. The
            input itself is still decoded (preview, meters) unless it is set to skip decoding.
        </div>
        <div v-if="encode.passthrough" class="mt-2.5">
            <label
                >Delay buffer sized for kbit/s
                <span class="text-fg-3">(empty = 25000, then the source's measured rate)</span></label
            ><input
                v-model.number="bufferK"
                type="number"
                min="0"
                step="1000"
                placeholder="25000"
                data-test="buffer-k"
            />
            <div class="text-xs text-fg-3">
                The source's rate the delay buffer is sized for: its longest destination delay plus 15 s at this rate.
                Left empty, the node assumes 25000 and grows the buffer once it has measured a faster source; set the
                rate when it is known, so a long delay holds from the start.
            </div>
        </div>
    </div>
    <template v-if="!encode.passthrough">
        <EncodeSettings
            v-if="!encode.twitch"
            v-model:video="encode.video"
            v-model:audio="encode.audio"
            v-model:advanced="encode.advanced"
            part="main"
            :gpus="gpus"
            :ffopts="ffopts"
            :use="use"
        />
        <VerticalSettings
            v-if="!encode.twitch"
            v-model="encode.video"
            :media="media"
            :in-w="inputFormat.w"
            :in-h="inputFormat.h"
        />
        <LadderSettings v-if="!encode.twitch" v-model="encode.video" />
        <template v-if="!encode.passthrough">
            <div class="mb-2.5">
                <label>On signal loss (this encode)</label>
                <select v-model="lossChoice">
                    <option value="">as the input shows it (black, or the input's picture)</option>
                    <option value="black">black; destinations stay connected</option>
                    <option v-for="m in media.filter((x) => x.kind === 'image')" :key="m.id" :value="'picture:' + m.id">
                        picture: {{ m.name }}
                    </option>
                    <option value="stop">
                        stop the encode: all its destinations disconnect until the signal returns
                    </option>
                </select>
            </div>
            <details v-if="!encode.twitch" :open="cuesOn">
                <summary>Ad markers (SCTE-35)</summary>
                <div class="mb-2.5">
                    <label class="flex items-center gap-1.5"
                        ><input v-model="cuesOn" type="checkbox" class="w-auto" data-test="encode-scte35" /> carry
                        SCTE-35 ad breaks, given from its detail panel, Companion or the API</label
                    >
                </div>
                <div v-if="encode.scte35" class="grid grid-cols-2 gap-2.5">
                    <div class="mb-2.5">
                        <label>Written as</label
                        ><select v-model="encode.scte35.mode" data-test="encode-scte35-mode">
                            <option value="splice_insert">splice_insert (a break and its length)</option>
                            <option value="time_signal">time_signal (placement opportunity)</option>
                        </select>
                    </div>
                    <div class="mb-2.5">
                        <label>Preroll <span class="text-fg-3">(ms, 0 = 4000)</span></label
                        ><input
                            v-model.number="encode.scte35.preroll_ms"
                            type="number"
                            min="0"
                            max="30000"
                            step="500"
                        />
                    </div>
                </div>
                <div v-if="encode.scte35 && inputHasCues" class="mb-2.5">
                    <label class="flex items-center gap-1.5"
                        ><input
                            :checked="!encode.scte35.ignore_source"
                            type="checkbox"
                            class="w-auto"
                            data-test="encode-scte35-source"
                            @change="
                                encode.scte35.ignore_source = !($event.target as HTMLInputElement).checked || undefined
                            "
                        />
                        carry the input's own cues too (an IP feed's SCTE-35, an SDI feed's SCTE-104), at their moments
                        (changes live)</label
                    >
                </div>
                <ScheduleEditor
                    v-if="encode.scte35"
                    v-model="encode.scte35.breaks"
                    what="a break"
                    label="Scheduled breaks"
                    started="cued"
                    :make="newBreak"
                    >Each break's cue goes out the preroll before its start, so the break starts then, and lasts to its
                    stop ({{ zone }}), even without central. A break whose start passes while the node or the encode is
                    down is skipped, and said in the event log. A break drops off once it has ended.</ScheduleEditor
                >
                <p class="text-xs text-fg-3">
                    A cue goes out the preroll before its break and again each second until then. SRT (caller), UDP,
                    RTP, RIST and TS recordings carry it; RTMP, MP4 and the node's own SRT listener and HLS pull do not.
                    Turning it on or off restarts the encode (its destinations reconnect); the rest changes live. Needs
                    the node's ffmpeg -ese6 or later.
                </p>
            </details>
            <details>
                <summary>Slate (this encode only)</summary>
                <div class="mb-2.5">
                    <label>Slate text</label
                    ><input v-model="encode.slate_text" :placeholder="encode.name || 'the encode\'s name'" />
                </div>
                <p class="text-xs text-fg-3">
                    Turn it on and off from the detail panel: drawn at the top of this encode only, without a restart.
                    The input's slate (bottom) is on every encode of the input.
                </p>
            </details>
        </template>
    </template>
    <details>
        <summary>Advanced</summary>
        <template v-if="!encode.passthrough && !encode.twitch">
            <EncodeSettings
                v-model:video="encode.video"
                v-model:audio="encode.audio"
                v-model:advanced="encode.advanced"
                part="advanced"
                :gpus="gpus"
                :ffopts="ffopts"
            />
        </template>
        <div class="mb-2.5">
            <label class="flex items-center gap-1.5"
                ><input v-model="encode.enabled" type="checkbox" class="w-auto" /> enabled</label
            >
        </div>
        <div class="mb-2.5" data-test="encode-probe">
            <div class="mb-1 text-xs text-fg-2">
                check what it sends, each as you choose: 10 s of it makes the encode degraded (changes live)
            </div>
            <div class="flex flex-wrap gap-x-4 gap-y-1">
                <label v-for="c in probeChecks" :key="c.key" class="flex items-center gap-1.5"
                    ><input
                        :checked="!!encode[c.key]"
                        type="checkbox"
                        class="w-auto"
                        :data-test="'encode-' + c.key.replace('_', '-')"
                        @change="encode[c.key] = ($event.target as HTMLInputElement).checked || undefined"
                    />
                    {{ c.label }}</label
                >
            </div>
        </div>
    </details>
    <div v-if="base && !encode.passthrough && !encode.twitch" class="mt-3">
        <button v-if="tplName === null" type="button" class="btn-ghost text-xs" @click="tplName = encode.name">
            Save these settings as an encoding template on central…
        </button>
        <template v-else>
            <label>Encoding template name</label>
            <div class="flex gap-2">
                <input v-model="tplName" placeholder="Twitch 1080p60 6M" @keydown.enter.prevent="saveEncTemplate" />
                <button type="button" :disabled="busy || !tplName.trim()" @click="saveEncTemplate">
                    {{ tplExisting ? 'Replace template' : 'Save template' }}
                </button>
                <button type="button" class="btn-ghost" @click="tplName = null">Cancel</button>
            </div>
            <div class="mt-1 text-xs text-fg-3">
                Video, audio and extra arguments, not the input or GPU. Every node can start an encode from it; encodes
                already made from a template keep their settings when it changes.
            </div>
        </template>
    </div>
</template>
